import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { Textarea } from './ui/textarea';
import {
  AlertTriangle, TrendingUp, Brain, Wrench, Clock, Users,
  Activity, Camera, Upload, Bell, Mail, Calendar,
  ChevronRight, Plus, Search, Filter, Eye, Download,
  Settings, Zap, Heart, Shield, CheckCircle, XCircle
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PredictiveAnalytics = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('near-miss');

  // Near-Miss State
  const [nearMissPredictions, setNearMissPredictions] = useState([]);
  const [nearMissTimeRange, setNearMissTimeRange] = useState('1year'); // alltime, 1year, 90days
  const [nearMissReport, setNearMissReport] = useState({
    location: '',
    activity: '',
    description: '',
    contributingFactors: '',
    potentialConsequences: ''
  });

  // Fatigue Detection State
  const [fatigueAnalyses, setFatigueAnalyses] = useState([]);
  const [fatigueUploadType, setFatigueUploadType] = useState('image'); // image or video
  const [selectedFatigueFile, setSelectedFatigueFile] = useState(null);
  const [fatigueShiftHours, setFatigueShiftHours] = useState(8);

  // Equipment State
  const [equipment, setEquipment] = useState([]);
  const [equipmentPredictions, setEquipmentPredictions] = useState([]);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [newEquipment, setNewEquipment] = useState({
    name: '',
    category: 'Power Tools',
    manufacturer: '',
    model: '',
    serialNumber: '',
    purchaseDate: '',
    expectedLifespanHours: 1000,
    maintenanceIntervalHours: 100,
    currentHoursUsed: 0,
    location: '',
    operator: '',
    condition: 'Good'
  });

  // Email Alert Settings
  const [emailAlerts, setEmailAlerts] = useState({
    enabled: true,
    emails: [''],
    nearMissThreshold: 70,
    fatigueThreshold: 80,
    equipmentDaysBefore: [30, 14, 7]
  });

  const equipmentCategories = [
    'Hand Tools',
    'Power Tools',
    'PPE Equipment',
    'Scaffolding',
    'Vehicles',
    'Heavy Machinery',
    'Fall Protection'
  ];

  // Fetch Near-Miss Predictions
  const fetchNearMissPredictions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/predictive/near-miss?timeRange=${nearMissTimeRange}`);
      setNearMissPredictions(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch near-miss predictions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit Near-Miss Report
  const submitNearMissReport = async () => {
    if (!nearMissReport.location || !nearMissReport.description) {
      toast({
        title: "Validation Error",
        description: "Location and description are required",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/predictive/near-miss/report`, nearMissReport);
      toast({
        title: "Success",
        description: "Near-miss report submitted successfully"
      });
      setNearMissReport({
        location: '',
        activity: '',
        description: '',
        contributingFactors: '',
        potentialConsequences: ''
      });
      fetchNearMissPredictions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit near-miss report",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Fatigue Analyses
  const fetchFatigueAnalyses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/predictive/fatigue`);
      setFatigueAnalyses(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch fatigue analyses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Upload and Analyze Fatigue
  const analyzeFatigue = async () => {
    if (!selectedFatigueFile) {
      toast({
        title: "Validation Error",
        description: "Please select an image or video to analyze",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result.split(',')[1];
          
          const response = await axios.post(`${API}/predictive/fatigue/analyze`, {
            mediaType: fatigueUploadType,
            mediaData: base64Data,
            shiftHours: fatigueShiftHours
          });

          toast({
            title: "Success",
            description: `Fatigue analysis complete. Risk Score: ${response.data.riskScore}/100`
          });
          
          setSelectedFatigueFile(null);
          await fetchFatigueAnalyses();
        } catch (error) {
          console.error('Fatigue analysis error:', error);
          toast({
            title: "Error",
            description: error.response?.data?.detail || "Failed to analyze fatigue",
            variant: "destructive"
          });
        } finally {
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        toast({
          title: "Error",
          description: "Failed to read file. Please try again.",
          variant: "destructive"
        });
        setLoading(false);
      };
      
      reader.readAsDataURL(selectedFatigueFile);
    } catch (error) {
      console.error('File reader error:', error);
      toast({
        title: "Error",
        description: "Failed to process file",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  // Fetch Equipment
  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/predictive/equipment`);
      setEquipment(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch equipment",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Add Equipment
  const addEquipment = async () => {
    if (!newEquipment.name || !newEquipment.category) {
      toast({
        title: "Validation Error",
        description: "Name and category are required",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/predictive/equipment`, newEquipment);
      toast({
        title: "Success",
        description: "Equipment added successfully"
      });
      setShowAddEquipment(false);
      setNewEquipment({
        name: '',
        category: 'Power Tools',
        manufacturer: '',
        model: '',
        serialNumber: '',
        purchaseDate: '',
        expectedLifespanHours: 1000,
        maintenanceIntervalHours: 100,
        currentHoursUsed: 0,
        location: '',
        operator: '',
        condition: 'Good'
      });
      fetchEquipment();
      fetchEquipmentPredictions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add equipment",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Equipment Predictions
  const fetchEquipmentPredictions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/predictive/equipment/predictions`);
      setEquipmentPredictions(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch equipment predictions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Log Equipment Usage
  const logEquipmentUsage = async (equipmentId, hoursUsed) => {
    setLoading(true);
    try {
      await axios.post(`${API}/predictive/equipment/${equipmentId}/usage`, {
        hoursUsed: parseFloat(hoursUsed),
        date: new Date().toISOString()
      });
      toast({
        title: "Success",
        description: "Usage logged successfully"
      });
      fetchEquipment();
      fetchEquipmentPredictions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log usage",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Save Email Alert Settings
  const saveEmailAlerts = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/predictive/alert-settings`, emailAlerts);
      toast({
        title: "Success",
        description: "Email alert settings saved"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save alert settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Email Alert Settings
  const fetchEmailAlerts = async () => {
    try {
      const response = await axios.get(`${API}/predictive/alert-settings`);
      if (response.data) {
        setEmailAlerts(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch alert settings");
    }
  };

  useEffect(() => {
    fetchNearMissPredictions();
    fetchFatigueAnalyses();
    fetchEquipment();
    fetchEquipmentPredictions();
    fetchEmailAlerts();
  }, []);

  useEffect(() => {
    fetchNearMissPredictions();
  }, [nearMissTimeRange]);

  const getRiskColor = (score) => {
    if (score >= 80) return 'text-red-600 bg-red-50 border-red-200';
    if (score >= 60) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const getRiskBadge = (score) => {
    if (score >= 80) return <Badge className="bg-red-600">Critical</Badge>;
    if (score >= 60) return <Badge className="bg-orange-600">High</Badge>;
    if (score >= 40) return <Badge className="bg-yellow-600">Medium</Badge>;
    return <Badge className="bg-green-600">Low</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        {/* Stylized Header */}
        <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-xl">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-900/90 to-violet-900/85" />
          
          <div className="relative px-4 md:px-8 py-6 md:py-10">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3 md:mb-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Activity className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 px-2 md:px-3 py-1 text-xs">
                      <Brain className="w-3 h-3 mr-1" />
                      AI-Powered
                    </Badge>
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30 px-2 md:px-3 py-1 text-xs">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Predictive
                    </Badge>
                  </div>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3">
                  Predictive Analytics
                </h1>
                <p className="text-sm md:text-lg text-indigo-100/90 max-w-2xl">
                  AI-powered predictions for proactive safety management
                </p>
              </div>
              
              {/* Quick Stats in Header - Hidden on mobile */}
              <div className="hidden lg:flex gap-3">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <AlertTriangle className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{nearMissPredictions.filter(p => p.riskScore >= 70).length}</p>
                  <p className="text-xs text-indigo-200">High Risk</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <Activity className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{fatigueAnalyses.length}</p>
                  <p className="text-xs text-indigo-200">Fatigue</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <Wrench className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{equipmentPredictions.filter(p => p.daysUntilFailure <= 30).length}</p>
                  <p className="text-xs text-indigo-200">Equipment</p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 md:gap-3 mt-4 md:mt-6">
              <Button 
                onClick={() => setActiveTab('alerts')}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/30 text-sm md:text-base"
              >
                <Settings className="w-4 h-4 mr-2" />
                Alert Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Feature Navigation Buttons - Mobile Friendly */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <Button
            variant={activeTab === 'near-miss' ? 'default' : 'outline'}
            onClick={() => setActiveTab('near-miss')}
            className={`h-auto py-4 md:py-6 flex flex-col items-center gap-2 rounded-xl md:rounded-2xl transition-all ${
              activeTab === 'near-miss' 
                ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg border-0' 
                : 'bg-white hover:bg-orange-50 border-2 hover:border-orange-300'
            }`}
          >
            <AlertTriangle className={`w-6 h-6 md:w-8 md:h-8 ${activeTab === 'near-miss' ? 'text-white' : 'text-orange-500'}`} />
            <div className="text-center">
              <p className={`font-semibold text-sm md:text-base ${activeTab === 'near-miss' ? 'text-white' : 'text-gray-900'}`}>
                Near-Miss
              </p>
              <p className={`text-xs ${activeTab === 'near-miss' ? 'text-orange-100' : 'text-gray-500'}`}>
                Predictions
              </p>
            </div>
          </Button>

          <Button
            variant={activeTab === 'fatigue' ? 'default' : 'outline'}
            onClick={() => setActiveTab('fatigue')}
            className={`h-auto py-4 md:py-6 flex flex-col items-center gap-2 rounded-xl md:rounded-2xl transition-all ${
              activeTab === 'fatigue' 
                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg border-0' 
                : 'bg-white hover:bg-blue-50 border-2 hover:border-blue-300'
            }`}
          >
            <Activity className={`w-6 h-6 md:w-8 md:h-8 ${activeTab === 'fatigue' ? 'text-white' : 'text-blue-500'}`} />
            <div className="text-center">
              <p className={`font-semibold text-sm md:text-base ${activeTab === 'fatigue' ? 'text-white' : 'text-gray-900'}`}>
                Fatigue
              </p>
              <p className={`text-xs ${activeTab === 'fatigue' ? 'text-blue-100' : 'text-gray-500'}`}>
                Detection
              </p>
            </div>
          </Button>

          <Button
            variant={activeTab === 'equipment' ? 'default' : 'outline'}
            onClick={() => setActiveTab('equipment')}
            className={`h-auto py-4 md:py-6 flex flex-col items-center gap-2 rounded-xl md:rounded-2xl transition-all ${
              activeTab === 'equipment' 
                ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg border-0' 
                : 'bg-white hover:bg-purple-50 border-2 hover:border-purple-300'
            }`}
          >
            <Wrench className={`w-6 h-6 md:w-8 md:h-8 ${activeTab === 'equipment' ? 'text-white' : 'text-purple-500'}`} />
            <div className="text-center">
              <p className={`font-semibold text-sm md:text-base ${activeTab === 'equipment' ? 'text-white' : 'text-gray-900'}`}>
                Equipment
              </p>
              <p className={`text-xs ${activeTab === 'equipment' ? 'text-purple-100' : 'text-gray-500'}`}>
                Maintenance
              </p>
            </div>
          </Button>

          <Button
            variant={activeTab === 'alerts' ? 'default' : 'outline'}
            onClick={() => setActiveTab('alerts')}
            className={`h-auto py-4 md:py-6 flex flex-col items-center gap-2 rounded-xl md:rounded-2xl transition-all ${
              activeTab === 'alerts' 
                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg border-0' 
                : 'bg-white hover:bg-green-50 border-2 hover:border-green-300'
            }`}
          >
            <Bell className={`w-6 h-6 md:w-8 md:h-8 ${activeTab === 'alerts' ? 'text-white' : 'text-green-500'}`} />
            <div className="text-center">
              <p className={`font-semibold text-sm md:text-base ${activeTab === 'alerts' ? 'text-white' : 'text-gray-900'}`}>
                Alert
              </p>
              <p className={`text-xs ${activeTab === 'alerts' ? 'text-green-100' : 'text-gray-500'}`}>
                Settings
              </p>
            </div>
          </Button>
        </div>

        {/* Statistics Cards - 2 columns on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-orange-500 hover:shadow-lg transition-all">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">High-Risk</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">
                    {nearMissPredictions.filter(p => p.riskScore >= 70).length}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">Require attention</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-blue-500 hover:shadow-lg transition-all">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">Fatigue</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">{fatigueAnalyses.length}</p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">This week</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <Activity className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl md:rounded-2xl shadow-sm border-t-4 border-t-purple-500 hover:shadow-lg transition-all col-span-2 md:col-span-1">
            <CardContent className="pt-4 md:pt-6 p-3 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">Equipment Alerts</p>
                  <p className="text-xl md:text-3xl font-bold text-gray-900">
                    {equipmentPredictions.filter(p => p.daysUntilFailure <= 30).length}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 hidden md:block">Need attention soon</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <Wrench className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Tab Content */}
        <div className="space-y-4 md:space-y-6">
          {/* Near-Miss Predictions Tab */}
          {activeTab === 'near-miss' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Report Near-Miss */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Plus className="w-5 h-5 mr-2 text-orange-600" />
                  Report Near-Miss
                </CardTitle>
                <CardDescription>Document near-miss incidents for analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Location</Label>
                  <Input
                    value={nearMissReport.location}
                    onChange={(e) => setNearMissReport({...nearMissReport, location: e.target.value})}
                    placeholder="e.g., Building A, Floor 2"
                  />
                </div>
                <div>
                  <Label>Activity</Label>
                  <Input
                    value={nearMissReport.activity}
                    onChange={(e) => setNearMissReport({...nearMissReport, activity: e.target.value})}
                    placeholder="e.g., Scaffolding work"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={nearMissReport.description}
                    onChange={(e) => setNearMissReport({...nearMissReport, description: e.target.value})}
                    placeholder="Describe what happened..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Contributing Factors</Label>
                  <Textarea
                    value={nearMissReport.contributingFactors}
                    onChange={(e) => setNearMissReport({...nearMissReport, contributingFactors: e.target.value})}
                    placeholder="What factors contributed?"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Potential Consequences</Label>
                  <Textarea
                    value={nearMissReport.potentialConsequences}
                    onChange={(e) => setNearMissReport({...nearMissReport, potentialConsequences: e.target.value})}
                    placeholder="What could have happened?"
                    rows={2}
                  />
                </div>
                <Button onClick={submitNearMissReport} className="w-full" disabled={loading}>
                  Submit Report
                </Button>
              </CardContent>
            </Card>

            {/* Predictions List */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Brain className="w-5 h-5 mr-2 text-orange-600" />
                      Risk Predictions
                    </CardTitle>
                    <CardDescription>AI-analyzed high-risk scenarios</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={nearMissTimeRange === '90days' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNearMissTimeRange('90days')}
                    >
                      90 Days
                    </Button>
                    <Button
                      variant={nearMissTimeRange === '1year' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNearMissTimeRange('1year')}
                    >
                      1 Year
                    </Button>
                    <Button
                      variant={nearMissTimeRange === 'alltime' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNearMissTimeRange('alltime')}
                    >
                      All Time
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading predictions...</div>
                ) : nearMissPredictions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No predictions available. Submit near-miss reports to generate predictions.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {nearMissPredictions.sort((a, b) => b.riskScore - a.riskScore).slice(0, 10).map((prediction, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 ${getRiskColor(prediction.riskScore)}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">{prediction.scenario}</h3>
                              {getRiskBadge(prediction.riskScore)}
                            </div>
                            <p className="text-sm mb-2">{prediction.description}</p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="bg-white/50 px-2 py-1 rounded">
                                📍 {prediction.location}
                              </span>
                              <span className="bg-white/50 px-2 py-1 rounded">
                                ⏰ {prediction.timeOfDay}
                              </span>
                              <span className="bg-white/50 px-2 py-1 rounded">
                                🔧 {prediction.activity}
                              </span>
                            </div>
                          </div>
                          <div className="text-center ml-4">
                            <div className="text-3xl font-bold">{prediction.riskScore}</div>
                            <div className="text-xs">Risk Score</div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-current/20">
                          <p className="text-sm font-medium mb-1">Recommended Actions:</p>
                          <ul className="text-xs space-y-1">
                            {prediction.recommendations.map((rec, idx) => (
                              <li key={idx} className="flex items-start">
                                <ChevronRight className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* Fatigue Detection Tab */}
          {activeTab === 'fatigue' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Upload Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-blue-600" />
                  Analyze Fatigue
                </CardTitle>
                <CardDescription>Upload images/videos for AI analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Media Type</Label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant={fatigueUploadType === 'image' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFatigueUploadType('image')}
                      className="flex-1"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Image
                    </Button>
                    <Button
                      variant={fatigueUploadType === 'video' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFatigueUploadType('video')}
                      className="flex-1"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Video
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Upload {fatigueUploadType === 'image' ? 'Image' : 'Video'}</Label>
                  <Input
                    type="file"
                    accept={fatigueUploadType === 'image' ? 'image/*' : 'video/*'}
                    onChange={(e) => setSelectedFatigueFile(e.target.files[0])}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Shift Hours Worked</Label>
                  <Input
                    type="number"
                    value={fatigueShiftHours}
                    onChange={(e) => setFatigueShiftHours(parseFloat(e.target.value))}
                    min="0"
                    max="24"
                    step="0.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Helps contextualize fatigue indicators
                  </p>
                </div>
                <Button onClick={analyzeFatigue} className="w-full" disabled={loading || !selectedFatigueFile}>
                  Analyze Fatigue
                </Button>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Privacy:</strong> All faces are automatically blurred. Analysis is aggregate only.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Recent Analyses */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-blue-600" />
                  Recent Fatigue Analyses
                </CardTitle>
                <CardDescription>AI-detected fatigue indicators</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading analyses...</div>
                ) : fatigueAnalyses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No analyses yet. Upload images or videos to detect fatigue.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fatigueAnalyses.slice(0, 10).map((analysis, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 ${getRiskColor(analysis.riskScore)}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">
                                Analysis #{analysis.id?.slice(0, 8) || index + 1}
                              </h3>
                              {getRiskBadge(analysis.riskScore)}
                              <Badge variant="outline">{analysis.shiftHours}h shift</Badge>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">
                              {new Date(analysis.timestamp).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-center ml-4">
                            <div className="text-3xl font-bold">{analysis.riskScore}</div>
                            <div className="text-xs">Fatigue Score</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div className="bg-white/50 px-2 py-1 rounded">
                            Workers: {analysis.workerCount || 'N/A'}
                          </div>
                          <div className="bg-white/50 px-2 py-1 rounded">
                            Type: {analysis.mediaType}
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-current/20">
                          <p className="text-sm font-medium mb-2">Detected Indicators:</p>
                          <div className="flex flex-wrap gap-2">
                            {analysis.indicators?.map((indicator, idx) => (
                              <span key={idx} className="text-xs bg-white/70 px-2 py-1 rounded">
                                {indicator}
                              </span>
                            ))}
                          </div>
                          {analysis.recommendations && analysis.recommendations.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm font-medium mb-1">Recommendations:</p>
                              <ul className="text-xs space-y-1">
                                {analysis.recommendations.map((rec, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <ChevronRight className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* Equipment Maintenance Tab */}
          {activeTab === 'equipment' && (
          <div className="space-y-4 md:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg md:text-xl font-semibold">Equipment Registry</h2>
                <p className="text-sm text-gray-600">Manage equipment and predict maintenance needs</p>
              </div>
              <Button onClick={() => setShowAddEquipment(!showAddEquipment)} className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Add Equipment
              </Button>
            </div>

          {/* Add Equipment Form */}
          {showAddEquipment && (
            <Card className="border-2 border-purple-200 rounded-xl md:rounded-2xl">
              <CardHeader>
                <CardTitle>Add New Equipment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label>Equipment Name *</Label>
                    <Input
                      value={newEquipment.name}
                      onChange={(e) => setNewEquipment({...newEquipment, name: e.target.value})}
                      placeholder="e.g., Excavator #1"
                    />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <select
                      value={newEquipment.category}
                      onChange={(e) => setNewEquipment({...newEquipment, category: e.target.value})}
                      className="w-full border rounded-md p-2"
                    >
                      {equipmentCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Manufacturer</Label>
                    <Input
                      value={newEquipment.manufacturer}
                      onChange={(e) => setNewEquipment({...newEquipment, manufacturer: e.target.value})}
                      placeholder="e.g., Caterpillar"
                    />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input
                      value={newEquipment.model}
                      onChange={(e) => setNewEquipment({...newEquipment, model: e.target.value})}
                      placeholder="e.g., 320D"
                    />
                  </div>
                  <div>
                    <Label>Serial Number</Label>
                    <Input
                      value={newEquipment.serialNumber}
                      onChange={(e) => setNewEquipment({...newEquipment, serialNumber: e.target.value})}
                      placeholder="SN12345"
                    />
                  </div>
                  <div>
                    <Label>Purchase Date</Label>
                    <Input
                      type="date"
                      value={newEquipment.purchaseDate}
                      onChange={(e) => setNewEquipment({...newEquipment, purchaseDate: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Expected Lifespan (hours)</Label>
                    <Input
                      type="number"
                      value={newEquipment.expectedLifespanHours}
                      onChange={(e) => setNewEquipment({...newEquipment, expectedLifespanHours: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Maintenance Interval (hours)</Label>
                    <Input
                      type="number"
                      value={newEquipment.maintenanceIntervalHours}
                      onChange={(e) => setNewEquipment({...newEquipment, maintenanceIntervalHours: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Current Hours Used</Label>
                    <Input
                      type="number"
                      value={newEquipment.currentHoursUsed}
                      onChange={(e) => setNewEquipment({...newEquipment, currentHoursUsed: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={newEquipment.location}
                      onChange={(e) => setNewEquipment({...newEquipment, location: e.target.value})}
                      placeholder="e.g., Site A"
                    />
                  </div>
                  <div>
                    <Label>Primary Operator</Label>
                    <Input
                      value={newEquipment.operator}
                      onChange={(e) => setNewEquipment({...newEquipment, operator: e.target.value})}
                      placeholder="Operator name"
                    />
                  </div>
                  <div>
                    <Label>Current Condition</Label>
                    <select
                      value={newEquipment.condition}
                      onChange={(e) => setNewEquipment({...newEquipment, condition: e.target.value})}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={addEquipment} disabled={loading}>
                    Add Equipment
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddEquipment(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Equipment Predictions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-purple-600" />
                Maintenance Predictions
              </CardTitle>
              <CardDescription>Equipment requiring attention soon</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading predictions...</div>
              ) : equipmentPredictions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No predictions available. Add equipment to generate predictions.
                </div>
              ) : (
                <div className="space-y-4">
                  {equipmentPredictions
                    .filter(p => p.daysUntilFailure <= 30)
                    .sort((a, b) => a.daysUntilFailure - b.daysUntilFailure)
                    .map((prediction, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 ${
                          prediction.daysUntilFailure <= 7
                            ? 'bg-red-50 border-red-200'
                            : prediction.daysUntilFailure <= 14
                            ? 'bg-orange-50 border-orange-200'
                            : 'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold">{prediction.equipmentName}</h3>
                              <Badge variant="outline">{prediction.category}</Badge>
                              {prediction.daysUntilFailure <= 7 && (
                                <Badge className="bg-red-600">Urgent</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-3">
                              <div>
                                <p className="text-xs text-gray-600">Hours Used</p>
                                <p className="font-semibold">{prediction.currentHours}/{prediction.expectedLifespanHours}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Usage %</p>
                                <p className="font-semibold">
                                  {Math.round((prediction.currentHours / prediction.expectedLifespanHours) * 100)}%
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Since Maintenance</p>
                                <p className="font-semibold">{prediction.hoursSinceMaintenance}h</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Days Until</p>
                                <p className="font-semibold text-orange-600">{prediction.daysUntilFailure} days</p>
                              </div>
                            </div>
                            <p className="text-sm mb-2">{prediction.reason}</p>
                            <div className="mt-2">
                              <p className="text-sm font-medium mb-1">Recommended Actions:</p>
                              <ul className="text-xs space-y-1">
                                {prediction.recommendations.map((rec, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <Wrench className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Equipment List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="w-5 h-5 mr-2 text-purple-600" />
                All Equipment ({equipment.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading equipment...</div>
              ) : equipment.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No equipment registered yet. Click &quot;Add Equipment&quot; to get started.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold">Name</th>
                        <th className="text-left p-3 text-sm font-semibold">Category</th>
                        <th className="text-left p-3 text-sm font-semibold">Hours Used</th>
                        <th className="text-left p-3 text-sm font-semibold">Condition</th>
                        <th className="text-left p-3 text-sm font-semibold">Location</th>
                        <th className="text-left p-3 text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {equipment.map((eq, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-medium">{eq.name}</p>
                            <p className="text-xs text-gray-500">{eq.model}</p>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{eq.category}</Badge>
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{eq.currentHoursUsed}h</p>
                            <p className="text-xs text-gray-500">of {eq.expectedLifespanHours}h</p>
                          </td>
                          <td className="p-3">
                            <Badge
                              className={
                                eq.condition === 'Excellent' || eq.condition === 'Good'
                                  ? 'bg-green-100 text-green-700'
                                  : eq.condition === 'Fair'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-red-100 text-red-700'
                              }
                            >
                              {eq.condition}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm">{eq.location || 'N/A'}</td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const hours = prompt('Enter hours used:');
                                if (hours) logEquipmentUsage(eq.id, hours);
                              }}
                            >
                              Log Usage
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
          )}

          {/* Alert Settings Tab */}
          {activeTab === 'alerts' && (
          <div className="space-y-4 md:space-y-6">
          <Card className="rounded-xl md:rounded-2xl shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center text-base md:text-lg">
                <Bell className="w-5 h-5 mr-2 text-green-600" />
                Email Alert Configuration
              </CardTitle>
              <CardDescription className="text-sm">
                Set up automatic email alerts for predictions and risks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Email Alerts</p>
                  <p className="text-sm text-gray-600">Receive notifications for high-risk predictions</p>
                </div>
                <input
                  type="checkbox"
                  checked={emailAlerts.enabled}
                  onChange={(e) => setEmailAlerts({...emailAlerts, enabled: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>

              <div>
                <Label>Email Recipients</Label>
                <p className="text-xs text-gray-500 mb-2">Add multiple email addresses for alerts</p>
                {emailAlerts.emails.map((email, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        const newEmails = [...emailAlerts.emails];
                        newEmails[index] = e.target.value;
                        setEmailAlerts({...emailAlerts, emails: newEmails});
                      }}
                      placeholder="email@example.com"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newEmails = emailAlerts.emails.filter((_, i) => i !== index);
                        setEmailAlerts({...emailAlerts, emails: newEmails});
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEmailAlerts({...emailAlerts, emails: [...emailAlerts.emails, '']})}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Email
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Near-Miss Alert Threshold</Label>
                  <Input
                    type="number"
                    value={emailAlerts.nearMissThreshold}
                    onChange={(e) => setEmailAlerts({...emailAlerts, nearMissThreshold: parseInt(e.target.value)})}
                    min="0"
                    max="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Alert when risk score ≥ this value</p>
                </div>
                <div>
                  <Label>Fatigue Alert Threshold</Label>
                  <Input
                    type="number"
                    value={emailAlerts.fatigueThreshold}
                    onChange={(e) => setEmailAlerts({...emailAlerts, fatigueThreshold: parseInt(e.target.value)})}
                    min="0"
                    max="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Alert when fatigue score ≥ this value</p>
                </div>
                <div>
                  <Label>Equipment Alert Days</Label>
                  <div className="flex gap-2">
                    {[30, 14, 7].map(days => (
                      <Badge
                        key={days}
                        className={
                          emailAlerts.equipmentDaysBefore.includes(days)
                            ? 'bg-blue-600 cursor-pointer'
                            : 'bg-gray-300 cursor-pointer'
                        }
                        onClick={() => {
                          const current = emailAlerts.equipmentDaysBefore;
                          const updated = current.includes(days)
                            ? current.filter(d => d !== days)
                            : [...current, days].sort((a, b) => b - a);
                          setEmailAlerts({...emailAlerts, equipmentDaysBefore: updated});
                        }}
                      >
                        {days}d
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Alert at these intervals before failure</p>
                </div>
              </div>

              <Button onClick={saveEmailAlerts} disabled={loading}>
                <Mail className="w-4 h-4 mr-2" />
                Save Alert Settings
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Alert Types</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Near-Miss Predictions:</strong> Sent when AI identifies high-risk scenarios above threshold</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Fatigue Detections:</strong> Sent when worker fatigue exceeds safe levels</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Equipment Maintenance:</strong> Sent at 30, 14, and 7 days before predicted failure</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Critical Alerts:</strong> SMS notifications for immediate attention items</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictiveAnalytics;
